import React, { useCallback, useRef, useState } from 'react';
import { FiCheck, FiShield, FiX } from 'react-icons/fi';
import useUpdateRole from 'api/RoleResource/useUpdateRole';
import usePortalDialog from 'layouts/Admin2/usePortalDialog';
import SearchField from '../SearchField';
import { hasDistinctLabel, matchesPermission, permissionTitle } from '../permissionUtils';

export default function RolePermissionEditor({ roleId, roleName, permissionNames, permissions, triggerRef, onClose }) {
    const [selected, setSelected] = useState(() => new Set(permissionNames));
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const dialogRef = useRef(null);
    const { mutateAsync, isLoading } = useUpdateRole(roleId);
    const busy = saving || isLoading;
    const close = useCallback(() => { if (!savingRef.current) onClose(); }, [onClose]);
    usePortalDialog(true, close, dialogRef, triggerRef);
    const visible = permissions.filter((permission) => matchesPermission(permission, search) && (filter === 'all' || selected.has(permission.name)));
    const changed = selected.size !== permissionNames.length || permissionNames.some((name) => !selected.has(name));
    const toggle = (name) => setSelected((previous) => {
        const next = new Set(previous);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    const save = async (event) => {
        event.preventDefault();
        if (savingRef.current || !changed) return;
        savingRef.current = true;
        setSaving(true);
        setError('');
        try {
            await mutateAsync({ permissions: Array.from(selected) });
            onClose();
        } catch {
            setError('Unable to save permissions. Your changes are still here. Please try again.');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return <div className="access-dialog-backdrop">
        <section ref={dialogRef} className="access-dialog" role="dialog" aria-modal="true" aria-labelledby="access-editor-title" aria-describedby="access-editor-description">
            <header className="access-dialog-header"><span className="access-detail-icon"><FiShield aria-hidden="true" /></span>
                <div><span className="access-eyebrow">{roleName}</span><h2 id="access-editor-title">Edit permissions</h2></div>
                <button type="button" className="access-icon-button" aria-label="Close permission editor" disabled={busy} onClick={close}><FiX aria-hidden="true" /></button></header>
            <p id="access-editor-description" className="access-dialog-description">Choose what users with the {roleName} role can do. Changes apply to everyone with this role.</p>
            <form onSubmit={save} className="access-editor-form" aria-busy={busy}>
                <div className="access-editor-controls">
                    <SearchField label="Search available permissions" placeholder="Search by name or label…" value={search} onChange={setSearch} />
                    <div className="access-editor-toolbar"><div className="access-editor-filters" role="group" aria-label="Filter available permissions">
                        <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All {permissions.length}</button>
                        <button type="button" aria-pressed={filter === 'selected'} onClick={() => setFilter('selected')}>Selected {selected.size}</button>
                    </div><span className="access-editor-bulk"><button type="button" disabled={busy} onClick={() => setSelected(new Set([...selected, ...permissions.map((permission) => permission.name)]))}>Select all</button><button type="button" disabled={busy} onClick={() => setSelected(new Set())}>Clear all</button></span></div>
                </div>
                <div className="access-editor-list">
                    {visible.map((permission) => <label className="access-permission-option" key={permission.id || permission.name}>
                        <input type="checkbox" checked={selected.has(permission.name)} disabled={busy} onChange={() => toggle(permission.name)} />
                        <span><strong>{permissionTitle(permission)}</strong>{hasDistinctLabel(permission) && <code>{permission.name}</code>}</span>
                        {selected.has(permission.name) && <FiCheck aria-hidden="true" />}
                    </label>)}
                    {!visible.length && <div className="access-empty" role="status"><h3>{search ? 'No matching permissions' : 'No permissions selected'}</h3><p>{search ? 'Try another name or label.' : 'Switch to All to choose permissions.'}</p></div>}
                </div>
                {error && <p className="access-form-error" role="alert">{error}</p>}
                <footer className="access-dialog-footer"><div><strong>{selected.size}</strong> permissions selected{changed && <span className="access-unsaved">Unsaved changes</span>}</div>
                    <div className="access-editor-actions"><button type="button" className="portal-button" disabled={busy} onClick={close}>Cancel</button><button type="submit" className="portal-button portal-button-primary" disabled={busy || !changed}>{busy ? 'Saving…' : 'Save changes'}</button></div></footer>
            </form>
        </section>
    </div>;
}
